import type { ChangeEvent, FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import {
  Box,
  Button,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { useAtomValue } from 'jotai';

import { ActionPlanStatus } from 'src/constants/actionPlan';
import type { ActionPlan } from 'src/types/planAction';
import { getActionPlanStatus } from 'src/helper/helper';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import FollowUpInspirationsButton from 'src/components/followUpInspirations/components/FollowUpInspirationsButton';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import { touchedAtom } from 'src/components/planAction/PlanActionDetailPage/components/Action/atoms';

import DeleteConfirmationModal from './DeleteConfirmationModal';

export const ACTION_PLAN_META_STATUS_TEST_ID = 'action-plan-meta-status';
export const ACTION_PLAN_META_NAME_TEST_ID = 'action-plan-meta-name';
export const ACTION_PLAN_META_SAVE_BUTTON_TEST_ID = 'action-plan-meta-save';
export const ACTION_PLAN_META_DELETE_BUTTON_TEST_ID = 'action-plan-meta-delete';

interface PlanActionMetaProps {
  actionPlanValue: ActionPlan;
  editable: boolean;
  onDelete: () => void;
  onNameChange: (arg1: string) => void;
  onSave: () => void;
  showFollowUpInspirations: boolean;
}

const PlanActionMeta: FC<PlanActionMetaProps> = ({
  actionPlanValue,
  editable,
  onDelete,
  onNameChange,
  onSave,
  showFollowUpInspirations,
}) => {
  const { t } = useTranslation(['planAction', 'common']);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { errors } = usePlanActionContext();
  const touched = useAtomValue(touchedAtom);

  return (
    <Stack
      direction="row"
      spacing={5}
      sx={{
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="column"
        spacing={1}
        sx={{
          justifyContent: 'flex-end',
          flexGrow: 1,
        }}
      >
        <InputLabel htmlFor="action-plan-name">
          <Typography
            color="initial"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {`${t('planAction:ActionPlan.ActionPlanName')}*`}
          </Typography>
        </InputLabel>

        <OutlinedInput
          size="small"
          fullWidth
          id="action-plan-name"
          disabled={!editable}
          name="action-plan-name"
          onChange={
            ((event: ChangeEvent<HTMLInputElement>) =>
              onNameChange(event.target.value)) as (event: ChangeEvent) => void
          }
          placeholder={t('planAction:ActionPlan.PersonalInformationWarning')}
          type="text"
          value={actionPlanValue.name}
          error={!!errors && !!errors.name}
          data-test-id={ACTION_PLAN_META_NAME_TEST_ID}
        />
      </Stack>
      <Stack
        direction="column"
        spacing={2}
        data-test-id={ACTION_PLAN_META_STATUS_TEST_ID}
        sx={{
          justifyContent: 'flex-end',
          flexGrow: 1,
          pb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
        >
          {t('planAction:ActionPlan.ActionPlanStatus')}
        </Typography>

        <Box>
          {actionPlanValue.status
            ? t(
                `planAction:ActionPlan.Status.${getActionPlanStatus(
                  actionPlanValue.status,
                )}`,
              )
            : t(
                `planAction:ActionPlan.Status.${getActionPlanStatus(
                  ActionPlanStatus.NOT_STARTED,
                )}`,
              )}
        </Box>
      </Stack>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          flexGrow: 1,
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            mb: 1,
          }}
        >
          {showFollowUpInspirations && <FollowUpInspirationsButton />}
        </Stack>
        <Stack
          direction="column"
          sx={{
            alignItems: 'flex-end',
          }}
        >
          <Button
            variant="text"
            size="small"
            href="https://dpdhl.sharepoint.com/sites/eos_hub/Followup/SitePages/Follow-Up.aspx"
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('EosHubLink')}
          </Button>
          <Button
            variant="text"
            size="small"
            href="https://feedback.suite.dpdhl.com/efs/"
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('EfsToolLink')}
          </Button>
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button
            variant="ctaAlt"
            fullWidth
            startIcon={<SaveRounded />}
            data-test-id={ACTION_PLAN_META_SAVE_BUTTON_TEST_ID}
            disabled={!editable || !touched}
            onClick={onSave}
          >
            <span>{t('common:save')}</span>
          </Button>

          <Button
            variant="cta"
            color="secondary"
            fullWidth
            startIcon={<DeleteRounded />}
            data-test-id={ACTION_PLAN_META_DELETE_BUTTON_TEST_ID}
            disabled={!editable || !onDelete}
            onClick={() => setShowDeleteModal(true)}
          >
            <span>{t('common:delete')}</span>
          </Button>
        </Stack>

        {errors && (
          <ErrorMessage>
            {t('planAction:ActionPlan.FillOutMarkedFields')}
          </ErrorMessage>
        )}
      </Stack>
      <DeleteConfirmationModal
        actionPlanName={actionPlanValue.name}
        handleDelete={onDelete || (() => undefined)}
        onClose={() => setShowDeleteModal(false)}
        showModal={showDeleteModal}
      />
    </Stack>
  );
};

export default PlanActionMeta;
